// src/utils/contentHelpers.js
// Content processing and parsing utilities extracted from UnitViewer.js

/**
 * Parse content if it's in string format
 */
export const parseContent = (content) => {
    if (typeof content === 'string') {
        try {
            return JSON.parse(content);
        } catch (e) {
            return content;
        }
    }
    return content;
};

/**
 * Fisher-Yates shuffle algorithm for randomizing array
 */
export const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

/**
 * Arrange package units based on package type (randomized vs ordered)
 */
export const arrangePackageUnits = (packageData, units) => {
    if (!packageData || !units || units.length === 0) return units;

    // If package type is explicitly set, use it
    if (packageData.packageType === 'randomized') {
        return shuffleArray(units);
    } else if (packageData.packageType === 'ordered') {
        return [...units].sort((a, b) => (a.order || 0) - (b.order || 0));
    }

    // Auto-determine based on unit orders (fallback for older packages)
    const orders = units.map(unit => unit.order || 0);
    const uniqueOrders = [...new Set(orders)];

    if (uniqueOrders.length === 1) {
        // All units have same order - randomize
        return shuffleArray(units);
    } else {
        // Different orders - keep ordered
        return [...units].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
};

/**
 * Extract content from various unit formats
 */
export const extractContentFromUnit = (content) => {
    if (typeof content === 'string') {
        return content;
    } else if (content && typeof content === 'object') {
        // Check for pages array first
        if (content.pages && Array.isArray(content.pages) && content.pages.length > 0) {
            return content.pages[0];
        }
        // Check other possible content properties
        if (content.body) return content.body;
        if (content.html) return content.html;
        if (content.text) return content.text;
        if (content.content) return content.content;
        
        // Handle array content
        if (Array.isArray(content) && content[0]) {
            return content[0];
        }
        
        // Find any string property that might contain content
        const stringProps = Object.keys(content).filter(key => 
            typeof content[key] === 'string' && content[key].length > 10
        );
        if (stringProps.length > 0) {
            return content[stringProps[0]];
        }
    }
    return '';
};

/**
 * Process callouts in content
 */
export const processCallouts = (htmlContent, callouts, pageIndex) => {
    if (!callouts || callouts.length === 0) return htmlContent;
    
    let calloutsArray = [];
    if (Array.isArray(callouts)) {
        calloutsArray = callouts;
    } else if (typeof callouts === 'object') {
        calloutsArray = Object.entries(callouts).map(([text, content], index) => ({
            id: `legacy-${index}`,
            text,
            content,
            page: 0,
            createdAt: new Date().toISOString()
        }));
    }
    
    const pageCallouts = calloutsArray.filter(callout => 
        (callout.page !== undefined ? callout.page : 0) === pageIndex
    );
    
    if (pageCallouts.length === 0) return htmlContent;
    
    let processed = htmlContent;
    pageCallouts.forEach((callout) => {
        if (callout.text && callout.content) {
            const searchText = callout.text;
            const firstIndex = processed.indexOf(searchText);
            
            if (firstIndex !== -1) {
                const beforeText = processed.substring(0, firstIndex);
                const afterText = processed.substring(firstIndex + searchText.length);
                const escapedContent = callout.content.replace(/"/g, '&quot;');
                const replacement = `<span class="story-tooltip" data-tooltip="${escapedContent}">${searchText}</span>`;
                
                processed = beforeText + replacement + afterText;
            }
        }
    });
    
    return processed;
};

/**
 * Get or create randomized options for a quiz unit
 */
export const getRandomizedOptions = (unitId, originalOptions, existingRandomized = {}) => {
    if (!originalOptions || originalOptions.length === 0) return [];

    // If we already have randomized options for this unit, return them
    if (existingRandomized[unitId]) {
        return existingRandomized[unitId];
    }

    // Create new randomized options
    return shuffleArray(originalOptions);
};

/**
 * Create internal content for navigation (handles packages and multi-page stories)
 */
export const createInternalContent = (currentContent, packages, units) => {
    if (!currentContent) return [];

    const currentCode = currentContent.code;
    if (!currentCode) return [];

    // Get ALL content (units + packages) with the same code
    const allContent = [...units, ...packages];
    const sameCodeContent = allContent.filter(item => item.code === currentCode);

    // If we have multiple items with the same code, handle them appropriately
    if (sameCodeContent.length > 1) {
        // Check if current content is a package
        const currentPackage = packages.find(pkg => pkg.id === currentContent.id);

        if (currentPackage && currentPackage.units && currentPackage.units.length > 0) {
            // For packages, return the arranged units (already randomized/ordered based on package type)
            return currentPackage.units;
        } else {
            // For regular units with same code, sort by order
            return sameCodeContent.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
    }

    // Handle single item with multiple pages
    const singleItem = sameCodeContent[0];
    
    // Check if it's a multi-page story
    if (singleItem && (singleItem.type === 'story' || singleItem.type === 'discussion' || singleItem.type === 'Assignment')) {
        let pages = [];

        // Check for pages in different locations
        if (singleItem.pages && Array.isArray(singleItem.pages)) {
            pages = singleItem.pages;
        } else if (singleItem.content && singleItem.content.pages && Array.isArray(singleItem.content.pages)) {
            pages = singleItem.content.pages;
        }

        // Create page objects for pagination when we have multiple pages
        if (pages.length > 1) {
            return pages.map((page, index) => ({
                id: `${singleItem.id}_page_${index}`,
                content: page,
                title: singleItem.title,
                type: singleItem.type,
                code: singleItem.code,
                callouts: singleItem.callouts || [],
                pages: singleItem.pages, // Keep reference to all pages
                originalUnit: singleItem  // Keep reference to original unit
            }));
        }
    }

    // Check if single item is a package with units
    if (singleItem && singleItem.type === 'package' && singleItem.units && singleItem.units.length > 1) {
        return singleItem.units; // Units are already arranged based on package type
    }

    return []; // Single content item, no internal navigation needed
};